import { useState, useEffect } from 'react';

/**
 * Hook returning whether the viewport is below the mobile breakpoint.
 * Safe to call once at the top of a component; pick the image yourself.
 * Initializes to `false` (desktop) so it matches the prerendered/SSR markup,
 * then corrects after mount.
 * @param breakpoint - Width threshold for mobile (default: 768px)
 */
export const useIsMobile = (breakpoint: number = 768): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);

  return isMobile;
};

/**
 * Hook to return the appropriate image path based on screen size
 * @param desktopImage - Path to desktop image
 * @param mobileImage - Path to mobile image
 * @param breakpoint - Width threshold for mobile (default: 768px)
 * @returns The appropriate image path
 */
export const useResponsiveImage = (
  desktopImage: string,
  mobileImage: string,
  breakpoint: number = 768
): string => {
  const [imagePath, setImagePath] = useState<string>(
    typeof window !== 'undefined' && window.innerWidth < breakpoint
      ? mobileImage
      : desktopImage
  );

  useEffect(() => {
    const handleResize = () => {
      setImagePath(window.innerWidth < breakpoint ? mobileImage : desktopImage);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [desktopImage, mobileImage, breakpoint]);

  return imagePath;
};

/**
 * Get responsive hero image path (automatically adds -mobile suffix)
 * @param basePath - Base image path (e.g., '/images/hero/business-signage.webp')
 * @returns Object with desktop and mobile paths
 */
export const getResponsiveHeroImage = (basePath: string) => {
  const mobilePath = basePath.replace(/\.(png|webp)$/, '-mobile.$1');
  return {
    desktop: basePath,
    mobile: mobilePath,
  };
};
